import type { Box } from './curveGeometry';
import type { Camera, ZoomBounds } from './useCamera';

export const TABLE = {
  /** Side gutters of the page, and so the table's inset from both edges. */
  gutter: 40,
  /** Inner padding from the top-left of what is placed. */
  pad: 16,
  /** Above what is placed, so the corner control never sits on a card at rest. */
  top: 48,
  /** Under the bottom of what is placed. */
  bottom: 56,
  /** The table's bottom edge keeps this far above the window's. */
  floor: 20,
  step: 1.18,
};

export const TABLE_ZOOM: ZoomBounds = { min: 0.6, max: 1.75 };

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** The box around everything placed, in canvas units; null for nothing placed. */
export function boundsOf(boxes: Iterable<Box>): Bounds | null {
  let out: Bounds | null = null;
  for (const b of boxes) {
    out = out
      ? {
          minX: Math.min(out.minX, b.x),
          minY: Math.min(out.minY, b.y),
          maxX: Math.max(out.maxX, b.x + b.w),
          maxY: Math.max(out.maxY, b.y + b.h),
        }
      : { minX: b.x, minY: b.y, maxX: b.x + b.w, maxY: b.y + b.h };
  }
  return out;
}

/** The canvas point the table's top-left padding is measured from. */
export interface Origin {
  x: number;
  y: number;
}

/** What the cards take up, in canvas units, as it was when the table was fitted. */
export interface Span {
  w: number;
  h: number;
}

/** The extra room left of what is placed that centres it across a table [width] wide, where it fits
 *  at [zoom] with its padding; none where it does not. */
export function centreOffset(span: Span | null, zoom: number, width: number): number {
  if (!span || width <= 0) return 0;
  return Math.max(0, (width - span.w * zoom) / 2 - TABLE.pad);
}

/** The same above what is placed, for a table kept at its least height [min]. */
export function liftOffset(span: Span | null, zoom: number, min: number): number {
  if (!span) return 0;
  return Math.max(0, (min - span.h * zoom) / 2 - TABLE.top);
}

/** Where the camera sits in the page state: [origin] sits [TABLE.pad] in and [TABLE.top] down from
 *  the table's top-left, moved by [centre] and [lift] where what is placed is smaller than the table, and
 *  scrolled sideways by [pan]. */
export function pageCamera(origin: Origin | null, zoom: number, pan: number, centre = 0, lift = 0): Camera {
  return {
    zoom,
    x: TABLE.pad + centre - (origin?.x ?? 0) * zoom - pan,
    y: TABLE.top + lift - (origin?.y ?? 0) * zoom,
  };
}

/**
 * Where a camera over a [view] this wide and tall shows everything in [bounds]: at 100% when it
 * fits within [pad], else zoomed out as far as it takes, no further than [zoom]'s least. What does
 * not fit even then starts at the top-left, in reach by panning.
 */
export function framingCamera(bounds: Bounds | null, view: { w: number; h: number }, zoom: ZoomBounds): Camera {
  if (!bounds) return { x: 0, y: 0, zoom: 1 };
  const w = bounds.maxX - bounds.minX;
  const h = bounds.maxY - bounds.minY;
  const fit = Math.min((view.w - 2 * TABLE.pad) / w, (view.h - TABLE.top - TABLE.pad) / h, 1);
  const z = Math.max(zoom.min, fit);
  return {
    zoom: z,
    x: Math.max(TABLE.pad, (view.w - w * z) / 2) - bounds.minX * z,
    y: Math.max(TABLE.top, (view.h - h * z) / 2) - bounds.minY * z,
  };
}

/** The shortest the table gets: its bottom edge [TABLE.floor] above a window of [windowHeight],
 *  given the table's top in that window. */
export function minTableHeight(windowHeight: number, top: number): number {
  return Math.max(0, windowHeight - top - TABLE.floor);
}

/** As tall as what is placed holds, never shorter than [min]; [lift] is the room kept above it. */
export function tableHeight(bounds: Bounds | null, origin: Origin | null, zoom: number, min: number, lift = 0): number {
  if (!bounds || !origin) return min;
  return Math.max(min, lift + TABLE.top + (bounds.maxY - origin.y) * zoom + TABLE.bottom);
}

/** How far the table can scroll sideways inside itself. */
export function maxPan(bounds: Bounds | null, origin: Origin | null, zoom: number, width: number): number {
  if (!bounds || !origin) return 0;
  return Math.max(0, TABLE.pad * 2 + (bounds.maxX - origin.x) * zoom - width);
}

/** A card's top-left held inside the table's padding. The table is fitted to [origin] once, so a
 *  card let go above or left of it would sit outside the table; this keeps it showable without
 *  moving anything else. The [centre] and [lift] room the table leaves around what is placed is
 *  the table's too. */
export function keepInView(at: { x: number; y: number }, origin: Origin | null, zoom: number, centre = 0, lift = 0) {
  if (!origin) return at;
  return {
    x: Math.max(at.x, origin.x - (TABLE.pad + centre) / zoom),
    y: Math.max(at.y, origin.y - (TABLE.top + lift) / zoom),
  };
}

/**
 * Whether the table keeps a sideways swipe from the browser's back gesture. In the page state it is
 * the table's only once it has been scrolled off its left edge; in full canvas, only while content
 * lies beyond the left of the view, so at the content's left bound the swipe is free.
 */
export function claimsSideways(full: boolean, pan: number, bounds: Bounds | null, camera: Camera): boolean {
  if (!full) return pan > 0;
  return !!bounds && camera.x + bounds.minX * camera.zoom < TABLE.pad - 0.5;
}
